import React, { useState, useEffect, useRef } from "react";
import "./PlaylistDetails.css";
import { Playlist } from "../../types/PlaylistTypes";
import { ThemeKey, THEMES } from '../../types/ThemeTypes';
import ThemeSelector from './ThemeSelector';
import SongSearchComponent from './SongSearchComponent';
import SongListManager from './SongListManager';
import PlaylistFormFields from './PlaylistFormFields';
import { useSongSelection } from './hooks/useSongSelection';
import { usePlaylistValidation } from './hooks/usePlaylistValidation';
import { usePlaylistOperations } from './hooks/usePlaylistOperations';

interface EditPlaylistDetailsProps {
    playlistId: string;
    onSaveComplete: () => void;
    onCancel: () => void;
    registerSaveFunction: (saveFunction: () => Promise<boolean>) => void;
    onThemeChange: (theme: keyof typeof THEMES) => void;
}

const EditPlaylistDetails: React.FC<EditPlaylistDetailsProps> = ({
    playlistId,
    onSaveComplete,
    onCancel,
    registerSaveFunction,
    onThemeChange
}) => {
    const [playlist, setPlaylist] = useState<Playlist | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [playlistName, setPlaylistName] = useState('');
    const [description, setDescription] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState<keyof typeof THEMES>('SING');

    // Use custom hooks
    const songSelection = useSongSelection();
    const validation = usePlaylistValidation();
    const playlistOps = usePlaylistOperations();

    // Use ref to track if we've already registered the save function
    const hasRegisteredSaveFunction = useRef(false);
    const saveFunction = useRef<() => Promise<boolean>>();

    const handleNameChange = (name: string) => {
        setPlaylistName(name);
        validation.validateName(name);
    };

    const handleDescriptionChange = (desc: string) => {
        setDescription(desc);
        validation.validateDescription(desc);
    };

    const handleThemeChange = (theme: keyof typeof THEMES) => {
        setSelectedTheme(theme);
        if (onThemeChange) {
            onThemeChange(theme);
        }
    };

    const handleSaveChanges = async (): Promise<boolean> => {
        if (!playlist) return false;

        // Validate before saving
        if (!validation.validateAll(playlistName, description)) {
            return false;
        }

        try {
            await playlistOps.updatePlaylist(playlistId, {
                name: playlistName,
                description: description,
                is_public: isPublic,
                theme: selectedTheme,
                songs: songSelection.selectedSongs
            });

            if (onSaveComplete) {
                onSaveComplete();
            }

            return true;
        } catch (err) {
            console.error("Error saving playlist changes:", err);
            setError(err instanceof Error ? err.message : String(err));
            return false;
        }
    };

    // Store the latest version of the function in the ref
    useEffect(() => {
        saveFunction.current = handleSaveChanges;
    }, [playlist, playlistName, description, isPublic, selectedTheme, songSelection.selectedSongs, onSaveComplete, playlistId]);

    // Register the save function only once when the component mounts
    useEffect(() => {
        if (registerSaveFunction && !hasRegisteredSaveFunction.current) {
            const stableFunction = async () => {
                if (saveFunction.current) {
                    return saveFunction.current();
                }
                return false;
            };

            registerSaveFunction(stableFunction);
            hasRegisteredSaveFunction.current = true;
        }
    }, [registerSaveFunction]);

    // Load playlist details on mount
    useEffect(() => {
        const loadPlaylistDetails = async () => {
            if (!playlistId) return;

            setIsLoading(true);
            setError(null);

            try {
                const { playlist: playlistData, songs } = await playlistOps.loadPlaylistDetails(playlistId);

                setPlaylist(playlistData);
                setPlaylistName(playlistData.name);
                setDescription(playlistData.description || '');
                setIsPublic(playlistData.is_public);
                setSelectedTheme(playlistData.theme);

                // Set the songs in the song selection
                songSelection.setSongs(songs);
            } catch (err) {
                console.error("Error loading playlist details:", err);
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setIsLoading(false);
            }
        };

        loadPlaylistDetails();
    }, [playlistId]);

    if (isLoading) {
        return <div className="playlist-details loading">Loading playlist...</div>;
    }

    if (error || playlistOps.error) {
        return <div className="playlist-details error">Error: {error || playlistOps.error}</div>;
    }

    if (!playlist) {
        return <div className="playlist-details not-found">Playlist not found</div>;
    }

    return (
        <div className="playlist-details">
            <div className="playlist-header">
                <div className="playlist-edit-buttons">
                    <button
                        className="edit-button cancel-button"
                        onClick={onCancel}
                    >
                        <i className="fa fa-times" aria-hidden="true"></i>
                    </button>

                    <button
                        className="edit-button save-mode"
                        onClick={() => {
                            if (saveFunction.current) {
                                saveFunction.current().then(success => {
                                    if (success) {
                                        onSaveComplete();
                                    }
                                });
                            }
                        }}
                        disabled={!!validation.nameError || !!validation.descriptionError || playlistOps.isLoading}
                    >
                        <i className="fa fa-save" aria-hidden="true"></i>
                    </button>
                </div>
            </div>

            <div className="playlist-body">
                <PlaylistFormFields
                    name={playlistName}
                    description={description}
                    isPublic={isPublic}
                    onNameChange={handleNameChange}
                    onDescriptionChange={handleDescriptionChange}
                    onPublicToggle={setIsPublic}
                    nameError={validation.nameError}
                    descriptionError={validation.descriptionError}
                    disabled={playlistOps.isLoading}
                    isEditMode={true}
                />

                <ThemeSelector
                    selectedTheme={selectedTheme}
                    onThemeChange={handleThemeChange}
                />

                {playlistOps.isLoading && <div className="saving-indicator">Saving changes...</div>}

                <div className="songs-container">
                    <div className="song-search-section">
                        <SongSearchComponent
                            onSongSelect={songSelection.addSong}
                            selectedSongs={songSelection.selectedSongs}
                            placeholder="Search for songs to add"
                            disabled={playlistOps.isLoading}
                        />
                    </div>

                    <SongListManager
                        songs={songSelection.enrichedSongs}
                        onRemoveSong={songSelection.removeSong}
                        isEditable={true}
                        emptyMessage="No songs in this playlist yet."
                        showNumbers={true}
                    />
                </div>
            </div>
        </div>
    );
};

export default EditPlaylistDetails; 